import { IClaims, IOption } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  CREATE_CLAIM_GROUP,
  GET_CLAIM_GROUP_BY_ID,
  GLOBAL_DROP_DOWN,
  UPDATE_CLAIM_GROUP,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import * as Yup from "yup";

export interface IClaimGroupForm {
  id?: string;
  claim_group_name: string;
  claims: string[];
  is_active: TBitValue;
  created_by?: string;
  modified_by?: string;
  created_date?: Date;
  modified_date?: Date;
}

export const CLAIM_GROUP_FIELD_INITIAL_VALUES = {
  claim_group_name: "",
  claims: [],
  is_active: BIT_VALUE.One,
};

export const claimGroupFieldValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    claim_group_name: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("claim_group_name")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("claim_group_name")],
          ["max", "50"],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("claim_group_name")],
          ["min", "2"],
        ])
      ),
  });
export const handleSubmitClaimGroup = async (
  values: IClaimGroupForm,
  setLoading: TReactSetState<boolean>,
  id: string | undefined,
  handleClose: (status: boolean) => void
) => {
  setLoading(true);
  try {
    let resultClaimGroup;
    if (id) {
      resultClaimGroup = await UPDATE_CLAIM_GROUP(values, id);
    } else {
      resultClaimGroup = await CREATE_CLAIM_GROUP(values);
    }
    if (
      resultClaimGroup &&
      resultClaimGroup.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      sweetAlertSuccess({
        message: resultClaimGroup.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: resultClaimGroup?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleError(e, "handleSubmitClaimGroup");
  } finally {
    setLoading(false);
  }
};

export const getClaimNameDdl = async (
  setClaimName: TReactSetState<IOption<string>[]>
) => {
  try {
    const payload = {
      entity: "claims",
      data_value: "id",
      display_value: "claim_name",
    };
    const claimNameDDLList = await GLOBAL_DROP_DOWN(payload);
    if (
      claimNameDDLList &&
      claimNameDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      if (claimNameDDLList.data) {
        setClaimName(claimNameDDLList.data);
      } else {
        setClaimName([]);
      }
    } else {
      toastError(claimNameDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getClaimNameDdl");
  }
};

export const fetchClaimGroupById = async (
  id: string,
  handleClose: (status: boolean) => void,
  setClaimGroup: TReactSetState<IClaimGroupForm | undefined>,
  setAddClaims: TReactSetState<IClaims[]>
) => {
  try {
    const claimGroupData = await GET_CLAIM_GROUP_BY_ID(id);
    if (claimGroupData && claimGroupData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setClaimGroup(claimGroupData.data);
      setAddClaims(claimGroupData.data.claims);
    } else {
      handleClose(false);
      sweetAlertError({
        message: claimGroupData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchClaimGroupById");
  }
};
